import type { DateTime } from "luxon";
import { useRef, useState } from "react";
import { useCheckOverlappingShiftsAndPatch } from "@/components/staffing/hooks/useHandleCheckOverlappingShifts";
import { useAnalytics } from "@/contexts/analytics/hooks";
import { useHospitalData } from "@/contexts/HospitalDataProvider";
import { type GapsScopedShiftGroup, isSectionWriteBlocked } from "../gapsGroupSheets";
import { type GapsPostShiftWrite, PostOutcome, postShiftAnalytics, runPostWrite } from "./gapsPostShiftWrite";
import { type GapsPostShiftFormFields, useGapsPostShiftForm } from "./useGapsPostShiftForm";

/** The form's fields as the write sends them — the job only for a post by job, no unit for "any unit". */
const writeOf = (form: GapsPostShiftFormFields): GapsPostShiftWrite => ({
	neededNurses: form.neededNurses,
	shiftStart: form.shiftStart,
	shiftEnd: form.shiftEnd,
	selectedRoles: form.selectedRoles,
	jobId: form.postsByJob ? form.jobId : undefined,
	bonus: form.bonus,
	chargeOnly: form.chargeOnly,
	location: form.location === "" ? undefined : form.location,
});

/**
 * The post sheet's form and its one write (P3-9): the fields (`useGapsPostShiftForm`), posted through
 * `useCheckOverlappingShiftsAndPatch` (`runPostWrite`) — the overlap merge prompt, the post, the requests refresh and the
 * `post_shift*` analytics are all that hook's, unchanged.
 *
 * One post per opening at a time. The wrapped hook's loading flags cover only its mutations — it then awaits a
 * requests refresh with both flags already off — so this hook keeps its own in-flight state from the tap to the end
 * of the whole call: a second tap in that window would post a duplicate open shift and notify the staff twice. After a
 * post the sheet stays busy while it closes.
 *
 * Nothing is posted for a group whose section cannot say which job it is (`isSectionWriteBlocked`); the sheet says why.
 */
export const useGapsPostShift = ({ group, date, onPosted }: { group: GapsScopedShiftGroup | undefined; date: DateTime; onPosted: () => void }) => {
	const { track, trackError } = useAnalytics();
	const { chosenDepartment } = useHospitalData();
	const { prefill, isComplete, fields } = useGapsPostShiftForm({ group, date });
	const { checkForOverlapsAndMergeIfUserApproved, postLoading, patchLoading } = useCheckOverlappingShiftsAndPatch();
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const inFlightRef = useRef<boolean>(false);

	const isWriteBlocked = group !== undefined && isSectionWriteBlocked(group);
	const isPosting = isSubmitting || postLoading || patchLoading;
	const canPost = isComplete && !isWriteBlocked && !isPosting;

	// A plain function, not a `useCallback`: it reads the whole form, which is a new object every render, so a memoised
	// one would be rebuilt every render all the same. The Post button calls it inline.
	const post = async (): Promise<void> => {
		if (!canPost || inFlightRef.current) return;
		inFlightRef.current = true;
		setIsSubmitting(true);
		const write = writeOf(fields);
		const properties = postShiftAnalytics({
			departmentId: chosenDepartment.department_id,
			isPrefilled: prefill !== null,
			groupKey: group === undefined ? null : group.key,
			postsByJob: fields.postsByJob,
			jobId: fields.jobId,
			roleGroups: fields.roleGroups,
			write,
		});
		track("gaps_post_shift_start", properties);
		const outcome = await runPostWrite(checkForOverlapsAndMergeIfUserApproved, write);
		if (outcome === PostOutcome.Posted || outcome === PostOutcome.Merged) {
			track("gaps_post_shift_success", { ...properties, merged: outcome === PostOutcome.Merged });
			onPosted();
			return;
		}
		if (outcome === PostOutcome.Failed) trackError("gaps_post_shift_error", properties);
		else track("gaps_post_shift_merge_kept", properties);
		// Nothing was posted: the form can be sent again.
		inFlightRef.current = false;
		setIsSubmitting(false);
	};

	return { ...fields, isWriteBlocked, canPost, isPosting, post };
};

export type GapsPostShiftState = ReturnType<typeof useGapsPostShift>;
